import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../lib/auth';
import { tasksAPI, calendarAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Loader2, CalendarClock, CheckSquare, Trash2, Lock, MapPin, Clock, User, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = { todo: 'À faire', in_progress: 'À faire', postponed: 'Prolongée 📅', done: 'Terminée ✅' };
const PRIORITY_LABELS = { low: 'Basse', medium: 'Moyenne', high: 'Haute', urgent: 'Urgente' };

function fmt(dt) {
    if (!dt) return '—';
    try {
        const d = new Date(dt);
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) +
            (String(dt).length > 10 ? ' · ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');
    } catch { return String(dt).slice(0, 16); }
}

export function WallItemDialog({ open, refType, refId, onClose, onChanged }) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [item, setItem] = useState(null);
    const [saving, setSaving] = useState(false);
    const [postMode, setPostMode] = useState(false);
    const [reason, setReason] = useState('');
    const [newDate, setNewDate] = useState('');

    const isEvent = refType === 'event';

    const load = useCallback(() => {
        if (!open || !refId) return;
        setLoading(true); setItem(null); setPostMode(false); setReason(''); setNewDate('');
        const req = isEvent ? calendarAPI.getEvent(refId) : tasksAPI.getById(refId);
        req.then(r => setItem(r.data))
            .catch(() => toast.error('Élément introuvable (peut-être supprimé).'))
            .finally(() => setLoading(false));
    }, [open, refId, isEvent]);

    useEffect(() => { load(); }, [load]);

    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const canManage = !!item && (
        isAdmin ||
        item.created_by === user?.id ||
        item.assigned_to === user?.id ||
        (item.assignee_ids || []).includes(user?.id) ||
        (item.participant_ids || []).includes(user?.id)
    );

    const refresh = () => { load(); onChanged?.(); };

    const setTaskStatus = async (status) => {
        if (status === 'postponed') { setPostMode(true); return; }
        try {
            setSaving(true);
            await tasksAPI.update(refId, { status });
            toast.success('Tâche mise à jour'); refresh();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    const submitPostpone = async () => {
        if (!reason.trim()) { toast.error('Indiquez une raison'); return; }
        if (!newDate) { toast.error('Choisissez une nouvelle date plus lointaine'); return; }
        try {
            setSaving(true);
            await tasksAPI.update(refId, { status: 'postponed', postpone_reason: reason.trim(), due_date: newDate });
            toast.success('Tâche prolongée'); setPostMode(false); refresh();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm('Supprimer définitivement cet élément ?')) return;
        try {
            setSaving(true);
            if (isEvent) await calendarAPI.deleteEvent(refId); else await tasksAPI.delete(refId);
            toast.success('Supprimé'); onChanged?.(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    const cancelEvent = async () => {
        try {
            setSaving(true);
            await calendarAPI.updateEvent(refId, { status: 'cancelled' });
            toast.success('Événement annulé'); refresh();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="wall-item-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 pr-6">
                        {isEvent ? <CalendarClock className="w-5 h-5 text-blue-600" /> : <CheckSquare className="w-5 h-5 text-amber-600" />}
                        {isEvent ? 'Événement' : 'Tâche'}
                        {!canManage && item && (
                            <Badge variant="outline" className="ml-auto gap-1 text-slate-500"><Lock className="w-3 h-3" />Consultation</Badge>
                        )}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : !item ? (
                    <p className="py-8 text-center text-sm text-slate-500">Élément introuvable.</p>
                ) : (
                    <div className="space-y-4 text-sm">
                        <div>
                            <h3 className="text-base font-semibold text-slate-900" data-testid="wall-item-title">{item.title}</h3>
                            {item.description && <p className="mt-1 text-slate-600 whitespace-pre-wrap">{item.description}</p>}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            {isEvent ? (
                                <>
                                    <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" />{fmt(item.start_datetime)}</Badge>
                                    {item.status === 'cancelled' && <Badge className="bg-red-100 text-red-700">Annulé</Badge>}
                                    {item.location && <Badge variant="outline" className="gap-1"><MapPin className="w-3 h-3" />{item.location}</Badge>}
                                </>
                            ) : (
                                <>
                                    <Badge className="bg-amber-100 text-amber-700">{STATUS_LABELS[item.status] || item.status}</Badge>
                                    {item.priority && <Badge variant="outline">{PRIORITY_LABELS[item.priority] || item.priority}</Badge>}
                                    <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" />Éch. {fmt(item.due_date)}</Badge>
                                </>
                            )}
                        </div>

                        {!isEvent && item.status === 'postponed' && item.postpone_reason && (
                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-amber-800 text-xs flex gap-2">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                <span>Prolongée — {item.postpone_reason}</span>
                            </div>
                        )}

                        {(item.assigned_to_name || (item.assignee_ids?.length > 0)) && (
                            <p className="flex items-center gap-1 text-slate-500"><User className="w-4 h-4" />
                                {item.assigned_to_name || `${(item.assignee_ids || []).length} assigné(s)`}
                            </p>
                        )}

                        {/* ===== Gestion (créateur / assigné / admin) ===== */}
                        {canManage && !postMode && (
                            <div className="border-t pt-3 space-y-3">
                                {!isEvent ? (
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Statut</Label>
                                        <Select value={item.status === 'in_progress' ? 'todo' : item.status} onValueChange={setTaskStatus}>
                                            <SelectTrigger data-testid="wall-item-status"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="todo">À faire</SelectItem>
                                                <SelectItem value="postponed">Prolongée 📅</SelectItem>
                                                <SelectItem value="done">Terminée ✅</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                ) : (
                                    item.status !== 'cancelled' && (
                                        <Button variant="outline" className="w-full text-red-600 border-red-200" onClick={cancelEvent} disabled={saving} data-testid="wall-item-cancel-event">
                                            Annuler l'événement
                                        </Button>
                                    )
                                )}
                                <Button variant="ghost" className="w-full text-red-500 hover:text-red-600 gap-1.5" onClick={remove} disabled={saving} data-testid="wall-item-delete">
                                    <Trash2 className="w-4 h-4" /> Supprimer
                                </Button>
                            </div>
                        )}

                        {/* ===== Prolongation (raison + nouvelle date) ===== */}
                        {canManage && postMode && !isEvent && (
                            <div className="border-t pt-3 space-y-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Raison du prolongement <span className="text-red-500">*</span></Label>
                                    <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex : client absent…" data-testid="wall-item-postpone-reason" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Nouvelle date (plus lointaine) <span className="text-red-500">*</span></Label>
                                    <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                                        min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} data-testid="wall-item-postpone-date" />
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setPostMode(false)}>Retour</Button>
                                    <Button className="bg-amber-600 hover:bg-amber-700" onClick={submitPostpone} disabled={saving} data-testid="wall-item-postpone-confirm">Prolonger</Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
